/*
 * Scoped to the FAB's closed shadow root, mounted from fab.ts. Selectors only
 * match elements inside the shadow tree, so nothing here can leak into the
 * host page. Conversely, host CSS can't reach `.fab` (descendant selectors
 * don't cross shadow boundaries), but inherited properties (`color`,
 * `font-family`, `font-size`, `line-height`) DO cross by default – so `:host`
 * pins them explicitly to defend against host-page text styling bleeding in.
 *
 * Dynamic theming (background/foreground colors, left/right position) is
 * driven by custom properties + data attributes set on the shadow host in
 * fab.ts. Custom properties cross the shadow boundary normally because
 * they're an inherited property.
 *
 * Design: idle white bubble with a soft violet glow; on hover the circle
 * scales up (springy), the brand mark does a full spin, the glow expands and
 * pulses, and a tooltip slides in above. While the panel is open the mark
 * cross-fades into a close glyph.
 */

:host {
	--weloop-fab-glow: hsl(260deg 100% 69%);
	--weloop-fab-ink: #14141f;

	position: fixed;
	right: 26px;

	/* The insets are 0 unless the host page opts into `viewport-fit=cover`;
	   then they keep the launcher clear of the notch and the home bar. */
	bottom: calc(26px + env(safe-area-inset-bottom, 0px));

	/* Same layer as the panel iframe. The FAB host is appended after the
	 * iframe, so document order keeps it on top – like the pilot, where the
	 * open panel and the launcher share a layer and the launcher shows a
	 * close glyph. */
	z-index: 2147483647;
	font-family:
		system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
		sans-serif;
	font-size: 22px;
	line-height: 1;
	text-align: center;

	/* Defensive pinning against host-page inheritance. */
	color-scheme: light dark;
}

:host([data-position="left"]) {
	right: auto;
	left: 26px;
}

/* Host-driven visibility. Set when a page hides the launcher (`launcher: false`
 * or `weloop.hideLauncher()`) and drives the widget from its own UI. `display:
 * none` keeps the hidden FAB out of layout and the focus order. */
:host([data-hidden="true"]) {
	display: none;
}

.fab {
	position: relative;

	display: block;

	width: 64px;
	height: 64px;
	padding: 0;
	margin: 0;

	font: inherit;
	cursor: pointer;

	background: transparent;
	border: none;
	opacity: 1;
	transform: scale(1);

	transition:
		transform 0.2s ease,
		opacity 0.16s ease,
		visibility 0s 0s;
}

/* Hiding the launcher only empties its box – the host still occupies layout,
 * pinned over the panel edge the widget shares. A box with no painted content
 * is still hit-testable, so without this the launcher silently swallows every
 * click that lands on it, including the panel's last bottom-bar tab. */
:host([data-expanded="true"]) {
	pointer-events: none;
}

/* The panel owns the close affordance while open, so the bubble leaves the
 * corner entirely. `visibility` (delayed until the shrink finishes) also
 * drops it from the tab order and the accessibility tree. */
:host([data-expanded="true"]) .fab {
	visibility: hidden;
	opacity: 0;
	transform: scale(0.4);

	transition:
		transform 0.2s ease,
		opacity 0.16s ease,
		visibility 0s 0.2s;
}

/* Landing flight: on close the bubble drops in from the panel's top corner –
 * where the drawer's close cross sits – spinning down the viewport edge and
 * bouncing to rest, while the layers below morph the cross back into the
 * brand mark mid-flight. */
:host([data-closing="true"]) .fab {
	animation: weloop-arrive 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.fab:focus-visible {
	outline: 2px solid var(--weloop-fab-glow);
	outline-offset: 4px;
	border-radius: 50%;
}

/* Soft violet halo behind the circle. Expands and pulses on hover. */
.glow {
	position: absolute;
	inset: -10px;

	pointer-events: none;

	background: radial-gradient(
		circle,
		hsl(260deg 100% 69% / 60%),
		transparent 70%
	);
	border-radius: 50%;
	opacity: 0.5;

	transition:
		inset 0.28s ease,
		opacity 0.28s ease;
}

.circle {
	position: absolute;
	inset: 0;

	display: flex;
	align-items: center;
	justify-content: center;

	color: var(--weloop-fab-foreground);

	background: var(--weloop-fab);
	border-radius: 50%;
	box-shadow:
		0 8px 22px hsl(260deg 100% 69% / 28%),
		0 2px 6px rgb(0 0 0 / 8%);

	/* Springy pop – the overshooting bezier gives the scale a bounce. */
	transition:
		transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
		box-shadow 0.22s ease;

	@starting-style {
		transform: scale(0.85);
	}
}

/* Brand mark and close glyph are stacked layers inside the circle; the open
 * state cross-fades between them. */
.logo,
.cross {
	position: absolute;
	inset: 0;

	display: flex;
	align-items: center;
	justify-content: center;

	transition:
		transform 0.22s cubic-bezier(0.16, 1, 0.3, 1),
		opacity 0.16s ease;
}

.logo svg {
	width: 34px;
	height: auto;
	overflow: visible;
}

.cross svg {
	width: 24px;
	height: 24px;
}

.logo {
	opacity: 1;
	transform: scale(1);
	transform-origin: center;
}

.cross {
	opacity: 0;
	transform: scale(0.4) rotate(90deg);
}

/* Hover effects only for a pointer that can hover: a touch screen fires
 * `:hover` on tap and keeps it, so the bubble would stay enlarged and
 * spinning after the panel closes. On hover the glow expands and pulses,
 * the circle pops and the mark spins. */
@media (hover: hover) {
	.fab:hover .glow {
		inset: -20px;
		opacity: 1;
		animation: weloop-glow-pulse 1.1s ease-in-out infinite;
	}

	.fab:hover .circle {
		box-shadow:
			0 16px 36px hsl(260deg 100% 69% / 45%),
			0 4px 10px rgb(0 0 0 / 12%);
		transform: scale(1.16);
	}

	.fab:hover .logo {
		animation: weloop-spin 0.7s cubic-bezier(0.4, 0, 0.2, 1);
	}
}

/* Open state – swap the mark for the close glyph. */
:host([data-expanded="true"]) .logo {
	opacity: 0;
	transform: scale(0.4);
	animation: none;
}

:host([data-expanded="true"]) .cross {
	opacity: 1;
	transform: scale(1) rotate(0deg);
}

/* Hold the cross visible through the first stretch of the descent, then let
 * the ordinary cross-fade play – the morph happens in mid-air. */
:host([data-closing="true"]) .logo,
:host([data-closing="true"]) .cross {
	transition-delay: 0.3s;
}

/* Unread badge pinned to the bubble's top-right corner. Driven by
 * `data-state` from fab.ts: empty (hidden), dot (presence only), count
 * (numeric, capped at 9+). Hidden with the bubble while the panel is open. */
.badge {
	position: absolute;
	top: -2px;
	right: -2px;

	display: none;
	align-items: center;
	justify-content: center;

	min-width: 20px;
	height: 20px;
	padding: 0 5px;

	font-size: 11px;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
	line-height: 1;
	color: #fff;

	/* White 11px text needs 4.5:1 – #e04a7c only reaches 3.9:1. */
	background: #d0386c;
	border: 2px solid #fff;
	border-radius: 999px;
	box-shadow: 0 2px 6px rgb(0 0 0 / 18%);
}

.badge[data-state="count"] {
	display: flex;
}

.badge[data-state="dot"] {
	display: flex;
	width: 14px;
	min-width: 14px;
	height: 14px;
	padding: 0;
}

/* Tooltip above the bubble; slides in on hover, hidden while open. */
.tooltip {
	position: absolute;
	right: -4px;
	bottom: calc(100% + 14px);

	padding: 8px 14px;

	font-size: 13px;
	font-weight: 600;
	color: #fff;
	white-space: nowrap;

	pointer-events: none;

	background: var(--weloop-fab-ink);
	border-radius: 10px;
	box-shadow: 0 8px 20px rgb(0 0 0 / 20%);
	opacity: 0;
	transform: translateY(6px);

	transition:
		transform 0.2s cubic-bezier(0.16, 1, 0.3, 1),
		opacity 0.18s ease;
}

/* Caret pointing down at the bubble. */
.tooltip::after {
	position: absolute;
	right: 28px;
	bottom: -4px;

	width: 8px;
	height: 8px;
	content: "";

	background: var(--weloop-fab-ink);
	transform: rotate(45deg);
}

:host([data-position="left"]) .tooltip {
	right: auto;
	left: -4px;
}

:host([data-position="left"]) .tooltip::after {
	right: auto;
	left: 28px;
}

/* Keyboard focus shows the tooltip too – it is the only place the bubble
 * says what it is. */
.fab:focus-visible ~ .tooltip {
	opacity: 1;
	transform: translateY(0);
}

@media (hover: hover) {
	.fab:hover ~ .tooltip {
		opacity: 1;
		transform: translateY(0);
	}
}

:host([data-expanded="true"]) .tooltip {
	opacity: 0;
	transform: translateY(6px);
}

/* No tooltip flash while the bubble is mid-flight. */
:host([data-closing="true"]) .tooltip {
	opacity: 0;
}

/* A project with onboarding copy answers "what is this?" with the intro card
   instead. Two hover surfaces stacked on one bubble would fight. */
:host([data-intro="true"]) .tooltip {
	opacity: 0;
	transform: translateY(6px);
}

/* --- Floating (draggable) bubble -----------------------------------------
 * `data-floating` marks a bubble with chat-head drag enabled (fab.ts). The
 * host is then positioned through inline `left`/`top`; this transition is
 * what plays the springy glide to the nearest edge when the visitor lets
 * go. `data-dragging` kills it so the bubble tracks the finger 1:1. */

:host([data-floating="true"]) {
	transition:
		left 0.45s cubic-bezier(0.22, 1, 0.36, 1),
		top 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

:host([data-floating="true"][data-dragging="true"]) {
	transition: none;
}

/* Without this, touch drags scroll the host page instead of moving the
 * bubble. Scoped to floating mode so a plain bubble keeps default touch
 * behavior. */
:host([data-floating="true"]) .fab {
	touch-action: none;
}

:host([data-floating="true"][data-dragging="true"]) .fab {
	cursor: grabbing;
}

/* No tooltip flash while the bubble is mid-drag. */
:host([data-dragging="true"]) .tooltip {
	opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
	:host([data-floating="true"]) {
		transition: none;
	}
}

/* --- Launcher modes ------------------------------------------------------
 * `data-mode` + `--weloop-fab-margin` come from the published config
 * (fab.ts). The margin offsets the launcher from the edge it anchors to:
 * side edge for bubble and horizontal-tab, top edge for vertical-tab. */

:host([data-mode="bubble"]) {
	right: calc(var(--weloop-fab-margin, 26px) + env(safe-area-inset-right, 0px));
}

:host([data-mode="bubble"][data-position="left"]) {
	right: auto;
	left: calc(var(--weloop-fab-margin, 26px) + env(safe-area-inset-left, 0px));
}

:host([data-mode="vertical-tab"]) {
	top: var(--weloop-fab-margin, 20px);
	right: 0;
	bottom: auto;
}

:host([data-mode="vertical-tab"][data-position="left"]) {
	right: auto;
	left: 0;
}

:host([data-mode="horizontal-tab"]) {
	right: var(--weloop-fab-margin, 20px);
	bottom: 0;
}

:host([data-mode="horizontal-tab"][data-position="left"]) {
	right: auto;
	left: var(--weloop-fab-margin, 20px);
}

/* Tab shape shared by both tab modes: the button itself carries the theme
 * background – no glow halo, no inner circle. */
:host([data-mode="vertical-tab"]) .fab,
:host([data-mode="horizontal-tab"]) .fab {
	display: flex;
	gap: 8px;
	align-items: center;
	justify-content: center;

	width: auto;
	height: auto;

	font-size: 14px;
	font-weight: 600;
	color: var(--weloop-fab-foreground);

	background: var(--weloop-fab);
	box-shadow:
		0 6px 18px hsl(260deg 100% 69% / 22%),
		0 2px 6px rgb(0 0 0 / 12%);
}

@media (hover: hover) {
	:host([data-mode="vertical-tab"]) .fab:hover,
	:host([data-mode="horizontal-tab"]) .fab:hover {
		filter: brightness(0.96);
	}
}

:host([data-mode="vertical-tab"]) .fab:focus-visible,
:host([data-mode="horizontal-tab"]) .fab:focus-visible {
	border-radius: 4px;
}

:host([data-mode="horizontal-tab"]) .fab {
	/* Flush with the bottom edge: the tab grows under the home bar instead of
	   floating above it. */
	padding: 10px 18px calc(10px + env(safe-area-inset-bottom, 0px));
	border-radius: 12px 12px 0 0;
}

:host([data-mode="vertical-tab"]) .fab {
	flex-direction: column;
	padding: 18px 10px;
	border-radius: 12px 0 0 12px;
}

:host([data-mode="vertical-tab"][data-position="left"]) .fab {
	border-radius: 0 12px 12px 0;
}

:host([data-mode="vertical-tab"]) .tab-label {
	writing-mode: vertical-rl;
}

/* The logo sits inline inside the tab instead of filling a circle. */
:host([data-mode="vertical-tab"]) .logo,
:host([data-mode="horizontal-tab"]) .logo {
	position: static;
	font-size: 16px;
}

:host([data-mode="vertical-tab"]) .logo svg,
:host([data-mode="horizontal-tab"]) .logo svg {
	width: 18px;
}

/* Uploaded launcher icon image. It fills the whole bubble so the crop circle
 * the dashboard showed is exactly the circle the visitor sees – no ring of
 * bubble background around the image. */
.icon-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 50%;
}

:host([data-mode="vertical-tab"]) .icon-image,
:host([data-mode="horizontal-tab"]) .icon-image {
	width: 18px;
	height: 18px;
}

@keyframes weloop-spin {
	0% {
		transform: rotate(0deg) scale(1);
	}

	50% {
		transform: rotate(180deg) scale(1.12);
	}

	100% {
		transform: rotate(360deg) scale(1);
	}
}

/* 116px ≈ the gap between the panel header's cross and the bubble's resting
 * center, so the flight reads as the cross detaching from the panel. */
@keyframes weloop-arrive {
	0% {
		opacity: 0;
		transform: translateY(calc(-100vh + 116px)) rotate(-540deg) scale(0.45);
	}

	10% {
		opacity: 1;
	}

	62% {
		opacity: 1;
		transform: translateY(0) rotate(0deg) scale(1.14);
	}

	78% {
		opacity: 1;
		transform: translateY(-10px) rotate(0deg) scale(0.96);
	}

	100% {
		opacity: 1;
		transform: translateY(0) rotate(0deg) scale(1);
	}
}

@keyframes weloop-glow-pulse {
	0%,
	100% {
		opacity: 0.9;
		transform: scale(1);
	}

	50% {
		opacity: 1;
		transform: scale(1.14);
	}
}

@media (prefers-reduced-motion: reduce) {
	.fab:hover .logo,
	.fab:hover .glow,
	:host([data-closing="true"]) .fab {
		animation: none;
	}

	.fab,
	.circle,
	.glow,
	.logo,
	.cross,
	.tooltip {
		transition-duration: 0.01ms;
	}
}
